import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Buffer } from 'buffer';
import { setItem } from '@/helpers/common/localStorage';
import { REACT_APP_AUTH, REACT_APP_USER } from '@/utils/config';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { updateMask } from '@/helpers/masks/cpf';
import { Auth } from '@/model/Auth';
import api, { AxiosError } from '@/services/api';
import { path } from '@/navigation/path';

import { toast } from 'react-toastify';
import UserType from '@/model/UserType';
import { LoginContainer, States, FormInputName } from './ui';

export interface UserTypeOptions {
  value: string;
  label: string;
}

const userTypeOptions: UserTypeOptions[] = [
  { value: '0', label: 'Funcionário' },
  { value: '1', label: 'PDV' },
  { value: '2', label: 'Sub PDV' },
  { value: '4', label: 'Produtor' },
  { value: '6', label: 'Artista' },
];

export const LoginScreen: React.FC = (): JSX.Element => {
  const history = useHistory();
  const [state, setState] = useState<States>(States.default);
  const [types, setTypes] = useState<UserTypeOptions[]>([]);

  const [shouldShowPasswordToText, setShouldShowPasswordToText] = useState(false);

  const { formData, formErrors, setErrors, onChangeFormInput, isFormValid } = useForm({
    initialData: {
      document: '',
      userType: '',
      password: '',
    },
    validators: {
      document: [validators.required],
      userType: [validators.required],
      password: [
        validators.required,
        /*         validators.minLength(8),
        validators.maxLength(15),
        validators.hasPasswordOnlyNumberCharacteres, */
      ],
    },
    formatters: {
      document: updateMask,
    },
  });

  const handleOnCheckUserType = async (cpf: string): Promise<void> => {
    setState(States.loading);
    try {
      const { data } = await api.post<UserType[]>('/auth/user-type', {
        cpf,
      });
      if (data && data.length > 0) {
        const newTypes: UserTypeOptions[] = [];
        data.forEach(value => {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < userTypeOptions.length; i++) {
            if (value === Number(userTypeOptions[i].value)) {
              if (!newTypes.includes(userTypeOptions[i])) newTypes.push(userTypeOptions[i]);
              break;
            }
          }
        });
        setTypes(newTypes);

        if (newTypes.length === 1) {
          onChangeFormInput(FormInputName.userType)(`${newTypes[0].value}`);
        }
      }
    } catch (error) {
      toast.error('Falha ao localizar o tipo de usuário!');
    } finally {
      setState(States.default);
    }
  };

  const handleOnSubmit = async (): Promise<void> => {
    try {
      if (isFormValid()) {
        setState(States.loading);

        const payload = Buffer.from(
          `${formData[FormInputName.document]}:${formData[FormInputName.password]}`,
          'utf8',
        ).toString('base64');

        const { data } = await api.post<Auth>(
          '/auth',
          {
            grant_type: 'client_credentials',
            userType: formData[FormInputName.userType],
          },
          {
            headers: {
              Authorization: `Basic ${payload}`,
            },
          },
        );

        setItem(String(REACT_APP_AUTH), data);
        setItem(String(REACT_APP_USER), data.user);

        if (
          data.user.userType !== UserType.CONTRACTOR &&
          data.user.userType !== UserType.PDV &&
          data.user.userType !== UserType.SUB_PDV
        ) {
          history.push(path.Dashboard.itself);
        } else {
          history.push(path.Dashboard.Events.itself);
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.code && err.code === 'ERR_BAD_REQUEST') {
        setErrors({
          document: ['CPF ou Senha inválida'],
          password: ['CPF ou Senha inválida'],
        });
      } else {
        toast.error('Falha ao realizar login, tentar novamente mais tarde');
      }
    } finally {
      setState(States.default);
    }
  };

  const handleOnTogglePasswordToText = (): void =>
    setShouldShowPasswordToText(!shouldShowPasswordToText);

  const handleGoToForgotPassword = (): void => history.push(path.Initial.ForgetPassword);

  return (
    <LoginContainer
      state={state}
      shouldShowPasswordToText={shouldShowPasswordToText}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onCheckUserType={handleOnCheckUserType}
      types={types}
      onSubmit={handleOnSubmit}
      onTogglePasswordToText={handleOnTogglePasswordToText}
      onGoToForgotPassword={handleGoToForgotPassword}
    />
  );
};
